import React from "react";
import type { Project } from "../../../api/useProjects";
import { useProjectSource } from "../../../api/useSources";
import useActiveAnalysesStream from "../../../api/useActiveAnalysesStream";
import { isNonEmptyArray } from "diagnostics-lib/array";
import ScreenGraph from "@ui/Picto/ScreenGraph";
import t from "../../../i18n";
import { withKeyNotNull } from "diagnostics-lib/typescript";
import { comparatorBy } from "diagnostics-lib/comparator";
import { Navigate, Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import { css } from "@linaria/core";
import BlankStateV2 from "@ui/BlankStateV2";
import { useQuantities } from "../../../api/useQuantities";
import { colors, spacings } from "@ui/Theme";
import { PRIMARY_HEADER_HEIGHT_CSS_VARIABLE } from "@ui/Header/useHeaderHeight";
import Navigation from "./Navigation";
import { toPath } from "../../../router/helpers/path";
import { useSummaryDashboards } from "../../../api/useSummaryDashboards";
import { useDashboards } from "../Dashboards/hook/api/dashboard";
import { usePositionSummaryDashbardTabs } from "./hooks/usePositionTab";
import { useSettings } from "../../../api/useSettings";
import { useTranslations } from "../../../api/useTranslations";
import { DataProcessing } from "@ui/Illustration/react";
import { useUserCanEdit } from "../Dashboards/hook/useUserCanEdit";
import Button from "@ui/Button";
import { ShareBoxLine } from "@ui/IconV2/react";
import { useCreatePublishAndLinkDashboardCallback } from "./hooks/createPublishAndLinkDashboard";
import { plantStateBlockDashboardTemplate } from "./template/plantStateDashboard";

const ProjectSummary = () => {
  const project = useOutletContext<Project>();
  if (!project) throw new Error("no project");

  const navigationRef = React.useRef<HTMLDivElement>(null);
  const settings = useSettings();
  const navigate = useNavigate();
  const source = useProjectSource(project);
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const quantities = useQuantities(project.id);
  const quantitiesOfInterest = React.useMemo(
    () => quantities.filter(withKeyNotNull("qoiOrder")).sort(comparatorBy("qoiOrder")),
    [quantities],
  );
  const remoteSummaryDashboards = useSummaryDashboards(project.id);

  if (quantitiesOfInterest.length === 0) {
    return (
      <BlankStateV2
        picto={<ScreenGraph />}
        title={t("summary.empty.title")}
        onClick={() =>
          navigate(
            toPath("/projects/:projectId/settings", {
              projectId: project.id,
            }),
          )
        }
        buttonLabel={t("summary.empty.action")}
      />
    );
  }

  if (!isNonEmptyArray(activeAnalyses) || !source)
    return (
      <BlankStateV2
        picto={<ScreenGraph />}
        title={t("common.empty.activeAnalyses.title")}
        subtitle={t("common.empty.activeAnalyses.description")}
        onClick={() =>
          navigate(
            toPath("/projects/:projectId/measures", {
              projectId: project.id,
            }),
          )
        }
        buttonLabel={t("common.empty.activeAnalyses.action")}
      />
    );

  return (
    <>
      <div ref={navigationRef} className={navigationHeaderSticky}>
        {settings.HIDE_LEGACY_SUMMARY_PAGE && remoteSummaryDashboards.length > 0 && (
          <Navigation project={project} />
        )}
        {!settings.HIDE_LEGACY_SUMMARY_PAGE && <Navigation project={project} />}
      </div>
      <Outlet context={project} />
    </>
  );
};

export default React.memo(ProjectSummary);

export const SummaryNavigate = () => {
  const { projectId } = useParams();
  if (!projectId) throw new Error("no project");

  const userCanWrite = useUserCanEdit(projectId);
  const settings = useSettings();
  const translations = useTranslations();
  const remoteSummaryDashboards = useSummaryDashboards(projectId);
  const { dashboards: remoteDashboards } = useDashboards(projectId);

  const { summaryPos, positionSummaryDashboards } = usePositionSummaryDashbardTabs(
    projectId,
    remoteDashboards,
    remoteSummaryDashboards,
  );

  const createPublishAndLinkDashboard = useCreatePublishAndLinkDashboardCallback({
    projectId,
  });

  if (!settings.HIDE_LEGACY_SUMMARY_PAGE && summaryPos === 0) {
    return <Navigate to={toPath("/projects/:projectId/summary/summary", { projectId })} replace />;
  }

  if (positionSummaryDashboards.length > 0 && positionSummaryDashboards[0]?.id)
    return (
      <Navigate
        to={toPath("/projects/:projectId/summary/dashboard/:dashboardId", {
          projectId,
          dashboardId: positionSummaryDashboards[0]?.id,
        })}
      />
    );

  if (userCanWrite)
    return (
      <BlankStateV2
        picto={<DataProcessing size="l" />}
        title={t("summary.dashboard.popup.empty", translations)}
        subtitle={t("summary.dashboard.popup.empty.subtitle", translations)}
        actionsSlot={
          <div className={actions}>
            <Button
              variant="primary"
              onClick={() => createPublishAndLinkDashboard(plantStateBlockDashboardTemplate)}
            >
              {t("summary.dashboard.popup.template")}
            </Button>
            <Button
              variant="secondary"
              leftIcon={<ShareBoxLine />}
              onClick={() => createPublishAndLinkDashboard()}
            >
              {t("summary.dashboard.popup.create", translations)}
            </Button>
          </div>
        }
      />
    );

  return (
    <BlankStateV2
      picto={<DataProcessing size="l" />}
      title={t("summary.dashboard.popup.empty", translations)}
      subtitle={t("summary.dashboard.popup.empty.subtitle", translations)}
    />
  );
};

//#region CSS
const navigationHeaderSticky = css`
  padding: 0 ${spacings.xxl};
  background-color: ${colors.hydrogen};

  position: sticky;
  z-index: 3;
  top: calc(var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}));

  border-bottom: 1px solid;
  border-bottom-color: ${colors.titanium200};
`;
const actions = css`
  display: flex;
  gap: ${spacings.m};
`;
//#endregion CSS
